import { fg } from '@atlaskit/platform-feature-flags';
import { getThirdPartyFilterOptions } from '@atlassian/search-client';

import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import { type SelectFilterState } from '../../../../../common/constants/filters/select-filter/types';
import { ProductKeys, type ProductKeys3P } from '../../../../../common/constants/products';
import { type SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

import { i18n, type SalesforceStageKey } from './messages';

/**
 * Hardcoded Salesforce opportunity stage values used as a fallback
 * when the dynamic fetch from the aggregator fails or the flag is off.
 */
const salesforceStages: SalesforceStageKey[] = [
	'New',
	'Closed Won',
	'Lost',
	'Invalid Opp',
	'Closed Won (Auto Linked)',
	'Closed Lost (Send Survey)',
	'No Opportunity',
	'Develop Opportunity',
	'Qualify Opportunity',
];

const salesforceProducts: ProductKeys[] = [ProductKeys.Salesforce];
const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('salesforceStage');

const getHardcodedStageOptions = (config: CloudConfig): SelectFilterOption[] => {
	return salesforceStages.map((stage) => ({
		value: stage,
		trackingKey: stage,
		queryParamValue: stage,
		isDefault: false,
		label: config.intl?.formatMessage(i18n[stage]) ?? stage,
		product: config.product,
		isSelected: false,
		isSuggested: false,
	}));
};

const fetchDynamicStageOptions = async (config: CloudConfig): Promise<SelectFilterOption[]> => {
	const cloudId = config.cloudId;
	const datasourceId = config.selectedDatasource?.datasourceId;

	if (!cloudId || !datasourceId) {
		return getHardcodedStageOptions(config);
	}

	const result = await getThirdPartyFilterOptions({
		cloudId,
		datasourceId,
		aggAbsoluteUrl: config.aggAbsoluteUrl,
	});

	if (!result) {
		return getHardcodedStageOptions(config);
	}

	const opportunityStages = result.filters.find((f) => f.name === 'opportunityStages');
	if (!opportunityStages || opportunityStages.values.length === 0) {
		return getHardcodedStageOptions(config);
	}

	return opportunityStages.values.map((stage) => ({
		value: stage.name,
		trackingKey: stage.name,
		queryParamValue: stage.name,
		isDefault: false,
		label: stage.label ?? stage.name,
		product: config.product,
		isSelected: false,
		isSuggested: false,
	}));
};

const getStageOptions = async (config: CloudConfig): Promise<SelectFilterOption[]> => {
	if (fg('rovo-fps-3p-ux-salesforce-dynamic-stage-filter')) {
		return fetchDynamicStageOptions(config);
	}
	return getHardcodedStageOptions(config);
};

const fetchQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	const allOptions = await getStageOptions(config);
	return allOptions.filter((option) =>
		queryParams.find((queryParam) => option.value === queryParam),
	);
};

export const filter: Readonly<{
	id: 'salesforceStage';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'salesforceStage'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (
		state: unknown,
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'salesforceStage'>;
	getInitialState: (
		config: CloudConfig,
	) => import('../../../../../common/constants/filters/select-filter/types').SelectFilterMonad<'salesforceStage'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'salesforceStage',
	products: [...salesforceProducts, ...products3PFromConfig],
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'salesforceStages',
		fetcher: async (queryParams, config) => {
			return fetchQueryParams(queryParams, config);
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return getStageOptions(config);
		},
	},
});
